import { useEffect, useMemo, useState } from "react";

import { useContactCenterDimensions } from "../virtual-receptionist/useContactCenterDimensions";
import { OpenChat } from "./chatDock.types";

export const DOCK_GAP = 8;
export const DOCK_WINDOW_MAX_WIDTH = 384;
export const DOCK_WINDOW_MIN_WIDTH = 264;
export const DOCK_WINDOW_HEIGHT = 540;
export const DOCK_HEADER_HEIGHT = 48;
export const DOCK_OVERFLOW_WIDTH = 48;

interface ChatDockLayout {
  visible: OpenChat[];
  overflow: OpenChat[];
  windowWidth: number;
  rightOffset: number;
  windowHeight: number;
}

const useViewport = () => {
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 1280,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  }));

  useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return viewport;
};

const rowWidth = (count: number, width: number, withOverflow: boolean) => {
  if (count === 0) return 0;

  const overflowWidth = withOverflow ? DOCK_OVERFLOW_WIDTH + DOCK_GAP : 0;

  return count * width + (count - 1) * DOCK_GAP + overflowWidth;
};

export function useChatDockLayout(openChats: OpenChat[]): ChatDockLayout {
  const { width: viewportWidth, height: viewportHeight } = useViewport();
  const { width: shellWidth } = useContactCenterDimensions();

  const rightOffset = shellWidth + 32;

  return useMemo(() => {
    const available = Math.max(viewportWidth - rightOffset - 16, DOCK_WINDOW_MIN_WIDTH);

    const ordered = [...openChats].sort((a, b) => a.openedAt - b.openedAt);

    let visible = ordered;
    let overflow: OpenChat[] = [];

    while (visible.length > 1 && rowWidth(visible.length, DOCK_WINDOW_MIN_WIDTH, overflow.length > 0) > available) {
      overflow = [visible[0], ...overflow];
      visible = visible.slice(1);
    }

    const overflowWidth = overflow.length > 0 ? DOCK_OVERFLOW_WIDTH + DOCK_GAP : 0;
    const gaps = Math.max(visible.length - 1, 0) * DOCK_GAP;
    const room = available - overflowWidth - gaps;
    const rawWidth = visible.length > 0 ? Math.floor(room / visible.length) : DOCK_WINDOW_MAX_WIDTH;

    return {
      visible,
      overflow,
      windowWidth: Math.min(DOCK_WINDOW_MAX_WIDTH, Math.max(DOCK_WINDOW_MIN_WIDTH, rawWidth)),
      rightOffset,
      windowHeight: Math.min(DOCK_WINDOW_HEIGHT, Math.max(240, viewportHeight - 24)),
    };
  }, [openChats, viewportWidth, viewportHeight, rightOffset]);
}
